/**
 * Versioned localStorage repository for progress data.
 *
 * Persistence is deliberately simple for V1: a single JSON blob under one key,
 * carrying a schema version so future shape changes can migrate rather than
 * wipe a student's history. localStorage is single-device and can be cleared by
 * the browser or lost in private browsing; that is acceptable for one student
 * on a primary device.
 *
 * All reads/writes are defensive: a corrupt or unreadable store falls back to
 * empty progress rather than throwing, so a storage glitch never breaks the app.
 */

import type { PracticeSession } from "../app/practice-session";
import type { QuizResults } from "../app/quiz-session";
import type { ProgressRecorder } from "./record";
import {
  emptyProgress,
  addSession,
  addSubskillSnapshots,
  recordTopicOutcome,
  recordSubskillOutcome,
  masteryFor,
  subskillConcerns,
  subskillTrends,
  weeklySummary,
  PROGRESS_SCHEMA_VERSION,
  type ProgressData,
  type MasteryState,
  type SubskillConcern,
  type SubskillTrend,
  type WeeklySummary,
  type SessionRecord,
} from "./model";
import type { TopicId } from "../curriculum/curriculum";
import type { SubskillId } from "../curriculum/subskills";

const STORAGE_KEY = "axle6:progress:v1";

type Storage = Pick<globalThis.Storage, "getItem" | "setItem" | "removeItem">;

/** Migrate an unknown persisted blob to the current schema. */
function migrate(raw: unknown): ProgressData {
  if (!raw || typeof raw !== "object") return emptyProgress();
  const data = raw as Partial<ProgressData>;
  if (data.schemaVersion !== PROGRESS_SCHEMA_VERSION) {
    return emptyProgress();
  }
  return {
    schemaVersion: PROGRESS_SCHEMA_VERSION,
    topics: data.topics ?? {},
    subskills: data.subskills ?? {},
    sessions: data.sessions ?? [],
    subskillHistory: data.subskillHistory ?? [],
  };
}

export class ProgressRepository implements ProgressRecorder {
  private data: ProgressData;
  private readonly storage: Storage | null;
  private readonly now: () => number;

  constructor(storage: Storage | null = safeLocalStorage(), now = Date.now) {
    this.storage = storage;
    this.now = now;
    this.data = this.load();
  }

  private load(): ProgressData {
    if (!this.storage) return emptyProgress();
    try {
      const raw = this.storage.getItem(STORAGE_KEY);
      if (!raw) return emptyProgress();
      return migrate(JSON.parse(raw));
    } catch {
      return emptyProgress();
    }
  }

  private persist(): void {
    if (!this.storage) return;
    try {
      this.storage.setItem(STORAGE_KEY, JSON.stringify(this.data));
    } catch {
      // Quota or unavailable storage: keep working in-memory this session.
    }
  }

  // --- ProgressRecorder ---------------------------------------------------

  recordPractice(session: PracticeSession): void {
    const sum = session.summary();
    const topic = session.config.topic;
    const at = this.now();
    let firstTry = 0;
    const perSubskill = new Map<SubskillId, { correct: number; total: number }>();
    for (const o of sum.outcomes) {
      const usedHelp =
        o.hintsUsed > 0 || o.stepsRevealed > 0 || o.solutionRevealed;
      const eventual = o.correct;
      if (o.firstAttemptCorrect) firstTry++;
      recordTopicOutcome(this.data, topic, {
        firstTry: o.firstAttemptCorrect,
        eventual,
        family: o.question.family,
        usedHelp,
        at,
      });
      for (const sk of o.question.subskills) {
        recordSubskillOutcome(this.data, sk, eventual);
        tallySubskill(perSubskill, sk, eventual);
      }
    }
    addSession(this.data, {
      kind: "practice",
      at,
      topic,
      total: sum.total,
      correct: sum.correct,
      firstTry,
    });
    addSubskillSnapshots(this.data, at, perSubskill);
    this.persist();
  }

  recordQuiz(results: QuizResults): void {
    for (const item of results.missed) {
      recordTopicOutcome(this.data, item.question.topic, {
        firstTry: false,
        eventual: false,
        family: item.question.family,
        usedHelp: false,
        at: this.now(),
      });
      for (const sk of item.question.subskills) {
        recordSubskillOutcome(this.data, sk, false);
      }
    }
    for (const [topic, tally] of results.byTopic) {
      const correctCount = tally.correct;
      for (let i = 0; i < correctCount; i++) {
        recordTopicOutcome(this.data, topic, {
          firstTry: false,
          eventual: true,
          family: "quiz",
          usedHelp: false,
          at: this.now(),
        });
      }
    }
    for (const [sk, tally] of results.bySubskill) {
      for (let i = 0; i < tally.correct; i++) {
        recordSubskillOutcome(this.data, sk, true);
      }
    }
    const at = this.now();
    addSession(this.data, {
      kind: "quiz",
      at,
      topic: results.byTopic.size === 1 ? [...results.byTopic.keys()][0]! : "mixed",
      total: results.total,
      correct: results.correct,
      firstTry: 0,
    });
    const perSubskill = new Map<SubskillId, { correct: number; total: number }>();
    for (const [sk, tally] of results.bySubskill) {
      perSubskill.set(sk, { correct: tally.correct, total: tally.total });
    }
    addSubskillSnapshots(this.data, at, perSubskill);
    this.persist();
  }

  // --- Reads for the Progress screen --------------------------------------

  mastery(topic: TopicId): MasteryState {
    return masteryFor(this.data.topics[topic]);
  }

  concerns(): SubskillConcern[] {
    return subskillConcerns(this.data);
  }

  trends(): SubskillTrend[] {
    return subskillTrends(this.data);
  }

  weekly(): WeeklySummary {
    return weeklySummary(this.data, this.now());
  }

  raw(): ProgressData {
    return this.data;
  }

  /** Timestamped session history, oldest first. For the trend view. */
  sessions(): readonly SessionRecord[] {
    return this.data.sessions;
  }

  // --- Backup / restore ---------------------------------------------------

  exportJson(): string {
    return JSON.stringify(this.data, null, 2);
  }

  importJson(text: string): boolean {
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      return false;
    }
    if (
      !parsed ||
      typeof parsed !== "object" ||
      (parsed as Partial<ProgressData>).schemaVersion !== PROGRESS_SCHEMA_VERSION
    ) {
      return false;
    }
    this.data = migrate(parsed);
    this.persist();
    return true;
  }

  reset(): void {
    this.data = emptyProgress();
    this.storage?.removeItem(STORAGE_KEY);
  }
}

// Shared instance, set once at startup so screens can read progress.
let shared: ProgressRepository | null = null;

export function initProgressRepository(
  storage: Storage | null = safeLocalStorage(),
): ProgressRepository {
  shared = new ProgressRepository(storage);
  return shared;
}

export function getProgressRepository(): ProgressRepository | null {
  return shared;
}

/** Accumulate a per-subskill correct/total tally for one session. */
function tallySubskill(
  map: Map<SubskillId, { correct: number; total: number }>,
  sk: SubskillId,
  correct: boolean,
): void {
  const t = map.get(sk) ?? { correct: 0, total: 0 };
  t.total++;
  if (correct) t.correct++;
  map.set(sk, t);
}

/** localStorage if available (guards SSR/tests/node), else null. */
function safeLocalStorage(): Storage | null {
  try {
    if (typeof localStorage !== "undefined") return localStorage;
  } catch {
    /* access can throw in some privacy modes */
  }
  return null;
}
